import React, { useState } from 'react';
import { openCookieSettings } from '../utils/cookieConsent';

const SocialIcon: React.FC<{ href: string; children: React.ReactNode; label: string }> = ({ href, children, label }) => (
    <a
        href={href}
        className="w-10 h-10 rounded-xl bg-white border border-gray-200 shadow-xs flex items-center justify-center text-gray-600 hover:text-gray-900 hover:border-gray-400 hover:bg-gray-50 transition-all duration-200"
        target="_blank"
        rel="noreferrer"
        aria-label={label}
    >
        {children}
    </a>
);

const Footer: React.FC = () => {
    const [newsletterEmail, setNewsletterEmail] = useState('');
    const [newsletterSubmitted, setNewsletterSubmitted] = useState(false);

    const paymentLogos = [
        { file: 'Blik.webp', name: 'BLIK' },
        { file: 'VISA.webp', name: 'Visa' },
        { file: 'mastercard.webp', name: 'Mastercard' },
        { file: 'Apple Pay.webp', name: 'Apple Pay' },
        { file: 'google-pay-mark_800.webp', name: 'Google Pay' },
        { file: 'PayPo.webp', name: 'PayPo' },
        { file: 'PKO Bank Polski.webp', name: 'PKO Bank Polski' },
        { file: 'mBank .webp', name: 'mBank' },
        { file: 'ING.webp', name: 'ING Bank Śląski' },
        { file: 'santander.webp', name: 'Santander' },
        { file: 'Bank Pekao.webp', name: 'Bank Pekao' },
        { file: 'Millenium.webp', name: 'Millennium' },
        { file: 'Alior.webp', name: 'Alior Bank' },
        { file: 'Credit Agricole.webp', name: 'Credit Agricole' },
    ];

    const logoPath = (file: string) => `/flagi_metod_platnosci_Przelewy24/logotypy_bankow/${encodeURIComponent(file)}`;

    const handleNewsletterSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (newsletterEmail.trim()) {
            setNewsletterSubmitted(true);
            setNewsletterEmail('');
        }
    };

    return (
        <footer className="w-full mt-12 bg-white text-gray-800 border-t border-gray-200 safe-area-inset-bottom font-sans">
            {/* Top Value Propositions Banner - Full width */}
            <div className="border-b border-gray-200 bg-[#f8f9fa] w-full">
                <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 py-4 sm:py-5">
                    <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-3.5 sm:gap-4 lg:gap-6">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-white border border-gray-200 shadow-xs flex items-center justify-center text-[#4d7c0f] shrink-0">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.25V3.75m0 3.75l-4.5 4.5m4.5-4.5h-4.5" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M1 9.5l4-5h10v10H1z" />
                                </svg>
                            </div>
                            <div className="min-w-0">
                                <h4 className="text-gray-900 font-bold text-sm sm:text-base leading-tight">Dostawa DPD</h4>
                                <p className="text-gray-600 text-xs sm:text-sm mt-0.5 leading-tight">Ubezpieczona przesyłka kurierska</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-white border border-gray-200 shadow-xs flex items-center justify-center text-emerald-600 shrink-0">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                                </svg>
                            </div>
                            <div className="min-w-0">
                                <h4 className="text-gray-900 font-bold text-sm sm:text-base leading-tight">100% Gwarancji doboru</h4>
                                <p className="text-gray-600 text-xs sm:text-sm mt-0.5 leading-tight">Precyzyjne dopasowanie do auta</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-white border border-gray-200 shadow-xs flex items-center justify-center text-[#4d7c0f] shrink-0">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                                </svg>
                            </div>
                            <div className="min-w-0">
                                <h4 className="text-gray-900 font-bold text-sm sm:text-base leading-tight">Bezpieczne płatności</h4>
                                <p className="text-gray-600 text-xs sm:text-sm mt-0.5 leading-tight">Przelewy24, BLIK, karty, raty</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-white border border-gray-200 shadow-xs flex items-center justify-center text-amber-600 shrink-0">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-.655 0-1.303-.024-1.944-.072A2.215 2.215 0 0112 14.894V10.608c0-.97.616-1.813 1.5-2.097a17.58 17.58 0 016.75 0z" />
                                </svg>
                            </div>
                            <div className="min-w-0">
                                <h4 className="text-gray-900 font-bold text-sm sm:text-base leading-tight">Doradztwo techniczne</h4>
                                <p className="text-gray-600 text-xs sm:text-sm mt-0.5 leading-tight">Pn–Pt 9:00–17:00 • 511 347 433</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Footer Navigation Grid - Full width */}
            <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 py-8 sm:py-10 lg:py-12">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-8 xl:gap-12">
                    
                    {/* Brand & Company Info */}
                    <div className="md:col-span-2 lg:col-span-4 space-y-4">
                        <a href="/" className="inline-block transition-opacity hover:opacity-85" aria-label="FelgoCar — strona główna">
                            <img
                                src="/logo/felgocar-main-20261020.webp"
                                alt="FelgoCar.pl"
                                width="220"
                                height="74"
                                loading="lazy"
                                decoding="async"
                                className="h-9 sm:h-10 md:h-11 w-auto object-contain"
                            />
                        </a>
                        <p className="text-sm sm:text-base text-gray-600 leading-relaxed max-w-md">
                            Specjalistyczny sklep z felgami aluminiowymi, stalowymi i akcesoriami montażowymi z gwarancją prawidłowego doboru.
                        </p>
                        
                        <div className="text-xs sm:text-sm text-gray-600 space-y-1 pt-1">
                            <p className="text-gray-900 font-bold text-sm sm:text-base">FELGOCAR Karol Matusiewicz</p>
                            <p>ul. Wierzbowska 27, 16-407 Wiżajny</p>
                            <p className="text-gray-600">
                                <span className="text-gray-900 font-semibold">NIP:</span> 8441824941 &nbsp;•&nbsp; <span className="text-gray-900 font-semibold">REGON:</span> 200162334
                            </p>
                        </div>

                        {/* Social Media */}
                        <div className="pt-2 flex items-center gap-2.5">
                            <SocialIcon href="https://www.facebook.com/felgocar" label="Felgocar na Facebook">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4.5 w-4.5 sm:h-5 sm:w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v2.385z"/></svg>
                            </SocialIcon>
                            <SocialIcon href="https://www.instagram.com/felgocar" label="Felgocar na Instagram">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4.5 w-4.5 sm:h-5 sm:w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.162 6.162 6.162 6.162-2.759 6.162-6.162-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4s1.791-4 4-4 4 1.79 4 4-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.441 1.441 1.441 1.441-.645 1.441-1.441-.645-1.44-1.441-1.44z"/></svg>
                            </SocialIcon>
                            <SocialIcon href="https://x.com/felgocar" label="Felgocar na X (Twitter)">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4.5 w-4.5 sm:h-5 sm:w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                            </SocialIcon>
                        </div>
                    </div>

                    {/* Navigation Columns - On mobile displayed as a clean 2-column grid */}
                    <div className="md:col-span-2 lg:col-span-5 grid grid-cols-2 gap-6 sm:gap-8">
                        {/* Offer & Selection */}
                        <div className="space-y-3">
                            <h3 className="text-sm sm:text-base font-bold text-gray-900 uppercase tracking-wider border-l-3 border-[#84cc16] pl-2.5">
                                Oferta i dobór
                            </h3>
                            <ul className="space-y-2 text-xs sm:text-sm">
                                <li>
                                    <a href="/products" className="text-gray-600 hover:text-gray-900 hover:font-medium transition-colors flex items-center gap-1.5 py-0.5">
                                        <span className="text-[#84cc16] font-bold">›</span> Felgi aluminiowe
                                    </a>
                                </li>
                                <li>
                                    <a href="/felgi-do-samochodu" className="text-gray-600 hover:text-gray-900 hover:font-medium transition-colors flex items-center gap-1.5 py-0.5">
                                        <span className="text-[#84cc16] font-bold">›</span> Dobierz do auta
                                    </a>
                                </li>
                                <li>
                                    <a href="/wyszukiwacz" className="text-gray-600 hover:text-gray-900 hover:font-medium transition-colors flex items-center gap-1.5 py-0.5">
                                        <span className="text-[#84cc16] font-bold">›</span> Wyszukiwarka
                                    </a>
                                </li>
                                <li>
                                    <a href="/#staggered" className="text-gray-600 hover:text-gray-900 hover:font-medium transition-colors flex items-center gap-1.5 py-0.5">
                                        <span className="text-[#84cc16] font-bold">›</span> Zestawy 2+2
                                    </a>
                                </li>
                                <li>
                                    <a href="/galeria-klientow" className="text-gray-600 hover:text-gray-900 hover:font-medium transition-colors flex items-center gap-1.5 py-0.5">
                                        <span className="text-[#84cc16] font-bold">›</span> Galeria Klientów
                                    </a>
                                </li>
                                <li>
                                    <a href="/blog" className="text-gray-600 hover:text-gray-900 hover:font-medium transition-colors flex items-center gap-1.5 py-0.5">
                                        <span className="text-[#84cc16] font-bold">›</span> Poradniki i blog
                                    </a>
                                </li>
                            </ul>
                        </div>

                        {/* Customer Support & Policies */}
                        <div className="space-y-3">
                            <h3 className="text-sm sm:text-base font-bold text-gray-900 uppercase tracking-wider border-l-3 border-[#84cc16] pl-2.5">
                                Obsługa i pomoc
                            </h3>
                            <ul className="space-y-2 text-xs sm:text-sm">
                                <li>
                                    <a href="/contact" className="text-gray-600 hover:text-gray-900 hover:font-medium transition-colors flex items-center gap-1.5 py-0.5">
                                        <span className="text-[#84cc16] font-bold">›</span> Kontakt
                                    </a>
                                </li>
                                <li>
                                    <a href="/account" className="text-gray-600 hover:text-gray-900 hover:font-medium transition-colors flex items-center gap-1.5 py-0.5">
                                        <span className="text-[#84cc16] font-bold">›</span> Moje konto
                                    </a>
                                </li>
                                <li>
                                    <a href="/returns" className="text-gray-600 hover:text-gray-900 hover:font-medium transition-colors flex items-center gap-1.5 py-0.5">
                                        <span className="text-[#84cc16] font-bold">›</span> Zwroty i reklamacje
                                    </a>
                                </li>
                                <li>
                                    <a href="/terms" className="text-gray-600 hover:text-gray-900 hover:font-medium transition-colors flex items-center gap-1.5 py-0.5">
                                        <span className="text-[#84cc16] font-bold">›</span> Regulamin sklepu
                                    </a>
                                </li>
                                <li>
                                    <a href="/privacy" className="text-gray-600 hover:text-gray-900 hover:font-medium transition-colors flex items-center gap-1.5 py-0.5">
                                        <span className="text-[#84cc16] font-bold">›</span> Polityka prywatności
                                    </a>
                                </li>
                                <li>
                                    <button
                                        type="button"
                                        onClick={openCookieSettings}
                                        className="text-gray-600 hover:text-gray-900 hover:font-medium transition-colors flex items-center gap-1.5 text-left py-0.5"
                                    >
                                        <span className="text-[#84cc16] font-bold">›</span> Pliki cookies
                                    </button>
                                </li>
                                <li>
                                    <a href="/faq" className="text-gray-600 hover:text-gray-900 hover:font-medium transition-colors flex items-center gap-1.5 py-0.5">
                                        <span className="text-[#84cc16] font-bold">›</span> Pytania (FAQ)
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* Direct Contact & Newsletter */}
                    <div className="md:col-span-2 lg:col-span-3 space-y-4">
                        <div className="space-y-3">
                            <h3 className="text-sm sm:text-base font-bold text-gray-900 uppercase tracking-wider border-l-3 border-[#84cc16] pl-2.5">
                                Szybki kontakt
                            </h3>
                            <div className="space-y-2 text-sm">
                                <a
                                    href="tel:+48511347433"
                                    className="p-2.5 sm:p-3 rounded-xl bg-[#f8f9fa] border border-gray-200 hover:border-gray-300 hover:bg-gray-50 flex items-center gap-3 transition-colors group shadow-xs"
                                >
                                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[#84cc16]/15 text-[#4d7c0f] flex items-center justify-center shrink-0">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                                        </svg>
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-gray-900 font-bold text-sm sm:text-base tracking-wide group-hover:text-[#4d7c0f] transition-colors">+48 511 347 433</div>
                                        <div className="text-xs text-gray-500">Infolinia: Pn–Pt 9:00–17:00</div>
                                    </div>
                                </a>

                                <a
                                    href="mailto:sklep@felgocar.pl"
                                    className="p-2.5 sm:p-3 rounded-xl bg-[#f8f9fa] border border-gray-200 hover:border-gray-300 hover:bg-gray-50 flex items-center gap-3 transition-colors group shadow-xs"
                                >
                                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                                        </svg>
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-gray-900 font-semibold text-sm sm:text-base group-hover:text-emerald-700 transition-colors">sklep@felgocar.pl</div>
                                        <div className="text-xs text-gray-500">Odpowiadamy w 24h</div>
                                    </div>
                                </a>
                            </div>
                        </div>

                        {/* Newsletter Widget */}
                        <div className="p-3.5 sm:p-4 rounded-xl bg-[#f8f9fa] border border-gray-200 shadow-xs space-y-2">
                            <h4 className="text-sm sm:text-base font-bold text-gray-900 flex items-center gap-1.5">
                                <span className="text-[#4d7c0f]">✉</span> Newsletter rabatowy
                            </h4>
                            <p className="text-xs sm:text-sm text-gray-600 leading-snug">
                                Zapisz się i odbierz kod rabatowy na pierwsze zakupy felg.
                            </p>
                            {newsletterSubmitted ? (
                                <div className="text-xs sm:text-sm text-emerald-800 font-medium py-2 px-3 bg-emerald-100 rounded-lg border border-emerald-200 text-center">
                                    Dziękujemy za zapis! Kod rabatowy wyślemy na Twój e-mail.
                                </div>
                            ) : (
                                <form onSubmit={handleNewsletterSubmit} className="flex gap-2 pt-1">
                                    <input
                                        type="email"
                                        value={newsletterEmail}
                                        onChange={(e) => setNewsletterEmail(e.target.value)}
                                        placeholder="Twój adres e-mail"
                                        required
                                        aria-label="Adres email do newslettera"
                                        className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-xs sm:text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#84cc16] focus:ring-1 focus:ring-[#84cc16] w-full"
                                    />
                                    <button
                                        type="submit"
                                        className="rounded-lg bg-[#84cc16] hover:bg-[#65a30d] text-white font-bold text-xs sm:text-sm px-4 py-2 transition-all shrink-0 shadow-xs"
                                    >
                                        Zapisz
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>

                {/* Secure Payments & Bank Strip - Full Width with Enlarged Logos */}
                <div className="mt-10 pt-6 sm:pt-8 border-t border-gray-200 space-y-4 sm:space-y-5">
                    <div className="flex flex-col md:flex-row items-center md:items-start lg:items-center justify-between gap-4 text-center md:text-left">
                        <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 text-center sm:text-left">
                            <img
                                src="/Przelewy24_logo.webp"
                                alt="Przelewy24"
                                width="150"
                                height="52"
                                className="h-10 sm:h-12 w-auto object-contain shrink-0"
                                loading="lazy"
                            />
                            <div>
                                <h4 className="text-sm sm:text-base font-bold text-gray-900 uppercase tracking-wide">
                                    Bezpieczne płatności internetowe
                                </h4>
                                <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                                    BLIK, karty płatnicze, szybkie przelewy z polskich banków oraz raty PayPo
                                </p>
                            </div>
                        </div>

                        <p className="text-xs text-gray-500 text-center md:text-right shrink-0">
                            Operator: <span className="text-gray-800 font-semibold">PayPro SA (Przelewy24)</span>, ul. Pastelowa 8, 60-198 Poznań
                        </p>
                    </div>

                    {/* Bank & Payment Method Badges - Enlarged and responsive on mobile & desktop */}
                    <div className="grid grid-cols-3 xs:grid-cols-4 sm:flex sm:flex-wrap items-center justify-center sm:justify-start gap-2 sm:gap-3">
                        {paymentLogos.map((icon) => (
                            <div
                                key={icon.name}
                                className="h-12 sm:h-14 px-2.5 sm:px-4 py-1.5 bg-white rounded-xl border border-gray-200 shadow-xs flex items-center justify-center hover:border-gray-400 hover:shadow-sm transition-all sm:min-w-[92px]"
                                title={icon.name}
                            >
                                <img
                                    src={logoPath(icon.file)}
                                    alt={icon.name}
                                    width="100"
                                    height="45"
                                    className="h-8 sm:h-9 md:h-10 w-auto max-w-full max-h-full object-contain"
                                    loading="lazy"
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Sub-footer Bar - Full Width */}
                <div className="mt-8 pt-5 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                    <div>
                        © {new Date().getFullYear()} FELGOCAR. Wszelkie prawa zastrzeżone.
                    </div>

                    <div>
                        <a href="/admin" className="text-gray-600 hover:text-gray-950 font-medium transition-colors">
                            Panel administracyjny
                        </a>
                    </div>

                    <div className="flex items-center gap-1.5">
                        <span className="text-gray-500">Strona wykonana przez</span>
                        <a
                            href="https://ativstudio.pl/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-gray-800 hover:text-black font-semibold transition-colors"
                        >
                            <img
                                src="/logo/ativ-studio-20261022.webp"
                                alt="ATIV Studio"
                                width="18"
                                height="18"
                                loading="lazy"
                                decoding="async"
                                className="h-4.5 w-4.5 rounded object-contain"
                            />
                            <span>ATIV Studio</span>
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
